import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useMarket from "../useMarket";
import {
  LuActivity,
  LuBriefcase,
  LuChevronRight,
  LuEye,
  LuFileText,
  LuInbox,
  LuLogIn,
  LuMapPin,
  LuMic,
  LuSearch,
  LuSend,
  LuTriangleAlert,
  LuUpload,
  LuWandSparkles,
} from "react-icons/lu";
import {
  setProfileData,
  setTechSkills,
  setFileResume,
  setActivities,
  setJobsApplied,
  setSubscriptionNextAction,
  SetFeaturesToBlock,
} from "../../store/actions/actions";
import { Badge, Skel, Note } from "../components/Ui";
import "./Dashboard.css";

/*
 * Data behavior mirrors the legacy DashboardDark.js:
 *  - send_resume_details_front_end_v2  -> fileResume / featuresToBlock /
 *    subscriptionNextAction / profileData + local interview & recent-job counts
 *  - user_recent_activities_v2 (task: total_jobs_applied) -> jobsApplied
 *  - user_recent_activities_v2 (task: recent_activity)   -> activities
 *    (legacy fetched this on the dashboard too, with a real Bearer token —
 *    the shell bootstrap's copy uses the legacy Header's tokenless "Bearer")
 *  - get_skills_technologies_from_resume_v2 -> techSkills (70s retry on error)
 */
const FILE_API = "https://us-east1-foursssolutions.cloudfunctions.net/send_resume_details_front_end_v2";
const DASH_API = "https://us-east1-foursssolutions.cloudfunctions.net/user_recent_activities_v2";
const SKILLS_API = "https://us-east1-foursssolutions.cloudfunctions.net/get_skills_technologies_from_resume_v2";
const IP_API = "https://us-east1-foursssolutions.cloudfunctions.net/interview_prep_function_v2";
const STATS_TIMEOUT_MS = 12000;

const QUICK_ACTIONS = [
  { to: "/search-job", icon: LuSearch, label: "Search jobs", sub: "Browse scored matches" },
  { to: "/recording", icon: LuMic, label: "Practice interview", sub: "Voice session with feedback" },
  { to: "/job-match", icon: LuWandSparkles, label: "Tailor resume", sub: "Match a resume to a role" },
  { to: "/resume-upload", icon: LuUpload, label: "Upload resume", sub: "Refresh your profile" },
];

/* Activity strings arrive as "You applied for this Job ID: gh:hubspotjobs:8071689"
   — a raw feed id is meaningless to the person who applied. Deterministic ids
   carry the employer's board slug, so use it; otherwise say plainly that it was
   a job and let the row link through to the posting. */
const JOB_ID_RE = /Job ID:\s*([^\s]+?)=*\s*$/i;

export function jobIdIn(text) {
  const m = String(text || "").match(JOB_ID_RE);
  return m ? m[1] : null;
}

const ATS_LABEL = { gh: "Greenhouse", lever: "Lever", ashby: "Ashby", sr: "SmartRecruiters", wk: "Workable" };

export function readableActivity(text) {
  const raw = String(text || "").replace(/=+$/, "");
  const id = jobIdIn(raw);
  if (!id) return raw;
  const lead = raw.slice(0, raw.toLowerCase().indexOf("job id:")).replace(/\s*for this\s*$/i, "").trim();
  const verb = lead || "You applied";
  const parts = id.split(":");
  if (parts.length >= 3 && ATS_LABEL[parts[0]]) {
    const board = parts[1].replace(/[-_]/g, " ").replace(/jobs?$/i, "").trim();
    if (board) return `${verb} to a role at ${board.replace(/\b\w/g, (c) => c.toUpperCase())}`;
  }
  return `${verb} to a job`;
}

/* Recent activity arrives unsorted with only a relative "time_ago" string
   ("9 minutes ago", "147 days ago"), so parse it to elapsed-seconds to order
   newest-first. Unknown formats sort to the bottom. */
const AGO_UNIT_SEC = { sec: 1, min: 60, hour: 3600, hr: 3600, day: 86400, week: 604800, month: 2592000, year: 31536000 };
function agoSeconds(s) {
  const t = String(s || "").toLowerCase().trim();
  if (!t || t.includes("just now") || t.includes("moment")) return 0;
  const m = t.replace(/\ban?\b/, "1").match(/(\d+)\s*(sec|min|hour|hr|day|week|month|year)/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const key = m[2].startsWith("sec") ? "sec" : m[2].startsWith("min") ? "min" : m[2];
  return Number(m[1]) * (AGO_UNIT_SEC[key] || 1);
}

function activityIcon(text) {
  const t = String(text || "").toLowerCase();
  if (t.includes("interview")) return LuMic;
  if (t.includes("tailor")) return LuWandSparkles;
  if (t.includes("view")) return LuEye;
  if (t.includes("resume")) return LuFileText;
  if (t.includes("appl")) return LuSend;
  if (t.includes("job")) return LuBriefcase;
  if (t.includes("login") || t.includes("logged") || t.includes("sign")) return LuLogIn;
  return LuActivity;
}

/* Lovable EmptyState port — dashed tile by default, `bare` = the
   border-0/bg-transparent variant Lovable uses inside glass tiles. */
function EmptyBlock({ icon, title, sub, action, bare }) {
  return (
    <div className={bare ? "ld-empty ld-empty--bare" : "ld-empty"}>
      <span className="ld-empty-ico">{icon || <LuInbox size={20} />}</span>
      <h3 className="ld-empty-title">{title}</h3>
      <p className="ld-empty-sub">{sub}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((s) => s.auth.auth.email);
  const subscription = useSelector((s) => s.auth.auth.subscription);
  const {
    profileData,
    techSkills,
    fileResume,
    activities,
    jobsApplied,
    subscriptionNextAction,
    apiToken,
    apiTokenReady,
    detailedJob,
  } = useSelector((s) => s.profile);

  const [interviewCount, setInterviewCount] = useState("");
  const [recentJobCount, setRecentJobCount] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);
  /* one-shot: flips true after the first stats attempt settles (success OR
     fail), so the tiles show a skeleton only on first paint and never hang or
     re-flicker on retries */
  const [statsReady, setStatsReady] = useState(false);
  const [statsError, setStatsError] = useState("");
  const [skillsError, setSkillsError] = useState("");

  const aliveRef = useRef(true);
  const retryRef = useRef(null);
  useEffect(() => {
    /* reset on (re)mount — StrictMode's mount→cleanup→mount double-invoke would
       otherwise leave this false after the first cleanup, making every
       aliveRef-guarded state update (interview count, statsReady) a no-op */
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, []);

  /* ---------- stale-while-revalidate cache ----------
     The dashboard's main data call cold-starts slowly and (per user directive)
     we do NOT keep instances warm. So cache the last-known dashboard payload in
     localStorage, paint it instantly on load, and let the fetches below refresh
     it in the background — a cold backend never shows a blank/skeleton screen. */
  const dashCacheKey = userEmail ? `cs_dash_${userEmail}` : null;
  const readDashCache = () => {
    if (!dashCacheKey) return null;
    try {
      return JSON.parse(localStorage.getItem(dashCacheKey) || "null");
    } catch (e) {
      return null;
    }
  };
  const writeDashCache = (patch) => {
    if (!dashCacheKey) return;
    try {
      localStorage.setItem(dashCacheKey, JSON.stringify({ ...(readDashCache() || {}), ...patch }));
    } catch (e) {
      /* quota / serialize errors are non-fatal */
    }
  };

  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !userEmail) return;
    const c = readDashCache();
    if (!c) return;
    seededRef.current = true;
    if (c.interviewCount != null) setInterviewCount(c.interviewCount);
    if (c.recentJobCount != null) setRecentJobCount(c.recentJobCount);
    if (c.profileData && !profileData) dispatch(setProfileData(c.profileData));
    if (c.fileResume && !fileResume) dispatch(setFileResume(c.fileResume));
    if (c.subscriptionNextAction && !subscriptionNextAction) dispatch(setSubscriptionNextAction(c.subscriptionNextAction));
    if (c.techSkills && !(techSkills && Object.keys(techSkills).length > 1)) dispatch(setTechSkills(c.techSkills));
    if (Array.isArray(c.activities) && !(activities && activities.length)) dispatch(setActivities(c.activities));
    if (c.jobsApplied != null && !(jobsApplied > 0)) dispatch(setJobsApplied(c.jobsApplied));
    setStatsReady(true); // show cached numbers immediately instead of a skeleton
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  /* legacy: pending detailed-job selection redirects to the job page */
  useEffect(() => {
    if (detailedJob !== "") {
      navigate(`/job/detailed?id=${detailedJob}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedJob]);

  const memoizedjobsApplied = useMemo(() => jobsApplied && jobsApplied > 0, [jobsApplied]);
  const memoizedactivities = useMemo(() => activities && activities.length > 0, [activities]);

  /* --- send_resume_details_front_end_v2 (same payload/headers/dispatches as legacy).
     Hardened: an abort timeout so a hung request can never leave the stat tiles
     on a permanent skeleton, plus one automatic retry before surfacing the error. --- */
  const statsRetryRef = useRef(0);
  const fetchFilenames = async () => {
    const controller = new AbortController();
    const killer = setTimeout(() => controller.abort(), STATS_TIMEOUT_MS);
    try {
      setStatsLoading(true);
      setStatsError("");
      const queryObj = { emailid: `${userEmail}` };
      const response = await fetch(FILE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(queryObj),
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.json();
      const extractedFilenames = data.file_details.map((file) => file.file_name);
      dispatch(setFileResume(extractedFilenames[0]));
      dispatch(SetFeaturesToBlock(data.features_to_block));
      dispatch(setSubscriptionNextAction(data.subscription_next_action));
      const extractedProfileData = data.user_details[0];
      dispatch(setProfileData(extractedProfileData));
      writeDashCache({
        interviewCount: data.total_interviews,
        recentJobCount: data.recent_job_applications,
        profileData: extractedProfileData,
        fileResume: extractedFilenames[0],
        subscriptionNextAction: data.subscription_next_action,
      });
      if (aliveRef.current) {
        setInterviewCount(data.total_interviews);
        setRecentJobCount(data.recent_job_applications);
        statsRetryRef.current = 0;
      }
    } catch (error) {
      if (!aliveRef.current) return;
      if (statsRetryRef.current < 1) {
        statsRetryRef.current += 1;
        retryRef.current = setTimeout(() => {
          if (aliveRef.current) fetchFilenames();
        }, 3000);
      } else {
        setStatsError(error.name === "AbortError" ? "The request timed out" : error.message);
      }
    } finally {
      clearTimeout(killer);
      if (aliveRef.current) {
        setStatsLoading(false);
        setStatsReady(true);
      }
    }
  };

  /* --- interview_prep_function_v2, task fetch_all_interviews (same call the
     Interview prep page makes). Powers the new practice-stats data points and
     doubles as a fallback source for the practiced count. --- */
  const [practiceStats, setPracticeStats] = useState(null);
  const fetchInterviewStats = async () => {
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({ email: userEmail, task: "fetch_all_interviews" }),
      });
      if (!response.ok) return;
      const data = await response.json();
      const text = Array.isArray(data.response) ? data.response : [];
      const video = Array.isArray(data.video_interview) ? data.video_interview : [];
      const rated = text
        .map((it) => Number(it?.overall_avg_rating))
        .filter((n) => Number.isFinite(n) && n > 0);
      const avgScore = rated.length
        ? Math.round((rated.reduce((a, b) => a + b, 0) / rated.length) * 10)
        : null;
      if (aliveRef.current) {
        setPracticeStats({ textCount: text.length, videoCount: video.length, avgScore });
      }
    } catch (error) {
      // stats are additive; the tile falls back to the legacy count silently
    }
  };

  /* --- user_recent_activities_v2, task recent_activity (legacy DashboardDark
     fetched this itself with a real Bearer token; skip when store has data) --- */
  const fetchActivities = async () => {
    if (memoizedactivities) return;
    try {
      const queryObj = { email_id: userEmail, task: "recent_activity" };
      const response = await fetch(DASH_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(queryObj),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.json();
      dispatch(setActivities(data.recent_activity));
      writeDashCache({ activities: data.recent_activity });
    } catch (error) {
      console.error("File Error:", error);
    }
  };

  /* --- user_recent_activities_v2, task total_jobs_applied (same guard as legacy) --- */
  const JobsApplied = async () => {
    if (memoizedjobsApplied) return;
    try {
      const queryObj = { email_id: userEmail, task: "total_jobs_applied" };
      const response = await fetch(DASH_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(queryObj),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.json();
      dispatch(setJobsApplied(data.total_jobs_applied));
      writeDashCache({ jobsApplied: data.total_jobs_applied });
    } catch (error) {
      if (aliveRef.current) setStatsError((prev) => prev || error.message);
    }
  };

  /* --- get_skills_technologies_from_resume_v2 (same guard + 70s retry as legacy) --- */
  const fetchSkillsTechnologies = async () => {
    if (techSkills && Object.keys(techSkills)?.length > 1) return;
    try {
      setSkillsError("");
      const fileName = fileResume;
      const bodyData = {
        email_id: userEmail,
        file_name: fileName,
        app_name: "frontend",
      };
      const response = await fetch(SKILLS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(bodyData),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch skills, technologies, and title");
      }
      const data = await response.json();
      dispatch(setTechSkills(data));
      writeDashCache({ techSkills: data });
    } catch (error) {
      if (!aliveRef.current) return;
      setSkillsError(error.message);
      retryRef.current = setTimeout(() => {
        if (aliveRef.current && fileResume?.length > 0) {
          fetchSkillsTechnologies();
        }
      }, 70000);
    }
  };

  useEffect(() => {
    if (userEmail !== "" && apiToken !== "") {
      fetchFilenames();
      JobsApplied();
      fetchActivities();
      fetchInterviewStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady]);

  useEffect(() => {
    if (fileResume?.length > 0 && apiToken !== "") {
      fetchSkillsTechnologies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileResume, apiTokenReady]);

  /* ---------- derived display data ---------- */
  const market = useMarket(); // India vs USA, from profile country
  const firstName =
    profileData?.first_name || (userEmail ? userEmail.split("@")[0] : "there");
  /* Pro signal: subscription may come from the login response
     (subscription_type) or localStorage (subscription) — the reducer accepts
     both — and, like legacy Subscriptions.js, subscriptionNextAction ===
     "Pro User" is also an authoritative pro signal. */
  const isPro =
    subscriptionNextAction === "Pro User" ||
    (subscription && String(subscription).toLowerCase() !== "free");
  /* Pro must win over a stale `subscription` string ("free" from the login
     response) — otherwise a Pro account showed a "Free plan" badge while the
     sidebar correctly showed Pro (Pavithra #4). */
  const planLabel = isPro
    ? "Pro"
    : subscription
    ? String(subscription).charAt(0).toUpperCase() + String(subscription).slice(1)
    : "Free";
  const hasResume = typeof fileResume === "string" && fileResume.length > 0;
  const activityList = (Array.isArray(activities) ? [...activities] : []).sort(
    (a, b) => agoSeconds(a?.time_ago) - agoSeconds(b?.time_ago)
  );
  const jobsAppliedDisplay =
    jobsApplied && !Array.isArray(jobsApplied) ? jobsApplied : 0;
  /* legacy parse of "recent_job_applications" sentence */

  const skillChips = useMemo(() => {
    const tech = Array.isArray(techSkills?.top_technologies) ? techSkills.top_technologies : [];
    const skills = Array.isArray(techSkills?.top_skills) ? techSkills.top_skills : [];
    const seen = new Set();
    const out = [];
    [...tech, ...skills].forEach((item) => {
      const name = typeof item === "string" ? item : item?.name;
      if (!name || seen.has(String(name).toLowerCase())) return;
      seen.add(String(name).toLowerCase());
      out.push({ name, proficiency: typeof item === "object" ? item?.proficiency : undefined });
    });
    return out.slice(0, 18);
  }, [techSkills]);
  const skillsExtractedCount = useMemo(() => {
    const tech = Array.isArray(techSkills?.top_technologies) ? techSkills.top_technologies.length : 0;
    const skills = Array.isArray(techSkills?.top_skills) ? techSkills.top_skills.length : 0;
    return tech + skills;
  }, [techSkills]);
  const skillsLoaded = techSkills && Object.keys(techSkills).length > 1;
  const skillsLoading = hasResume && !skillsLoaded && !skillsError;
  const activitiesMaybeLoading = activityList.length === 0 && (!apiTokenReady || statsLoading);

  return (
    <div className="stack-4" style={{ gap: 28 }}>
      {/* header — welcome + market + plan badge (Lovable header) */}
      <header className="row" style={{ flexWrap: "wrap", gap: 12 }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <h1 className="t-h2">Welcome back, {firstName}</h1>
          <p className="t-sm mt-1">
            {market.country === "IN"
              ? "Here is where your search across India stands today."
              : "Here is where your U.S. search stands today."}
          </p>
        </div>
        <span className="dash-market" title={`Showing ${market.label} opportunities`}>
          <LuMapPin size={13} /> {market.label}
        </span>
        <Badge tone={isPro ? "accent" : "plain"}>{planLabel} plan</Badge>
      </header>

      {(subscriptionNextAction === "Trial Expired" || subscriptionNextAction === "Reactivate") && (
        <Note tone="amber">
          <span>
            Your subscription needs attention — some features may be limited.{" "}
            <Link to="/subscription" style={{ fontWeight: 600, textDecoration: "underline" }}>
              Manage subscription
            </Link>
          </span>
        </Note>
      )}

      {statsError && (
        <div className="ld-error">
          <span className="ld-error-ico">
            <LuTriangleAlert size={20} />
          </span>
          <h3 className="ld-empty-title">Something went wrong</h3>
          <p className="ld-empty-sub">Couldn't load your dashboard details: {statsError}</p>
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-ghost btn-pill"
              onClick={() => {
                fetchFilenames();
                JobsApplied();
              }}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* section 1 — resume tile + stat tiles */}
      <section className="dash-cols">
        <div className="ld-glass" style={{ padding: 20 }}>
          <p className="ld-eyebrow">Resume on file</p>
          {!statsReady && !hasResume ? (
            <div className="row mt-2" style={{ gap: 12 }}>
              <Skel w={40} h={40} r={14} />
              <div className="grow stack-2" style={{ gap: 6 }}>
                <Skel w={200} h={15} />
                <Skel w={120} h={11} />
              </div>
            </div>
          ) : hasResume ? (
            <>
              <div className="row mt-2" style={{ flexWrap: "wrap", gap: 12 }}>
                <span className="ld-stat-icon">
                  <LuFileText size={20} />
                </span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <p
                    className="t-ink"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={fileResume}
                  >
                    {fileResume}
                  </p>
                </div>
                <Link to="/resume-upload" className="btn btn-ghost btn-pill">
                  Update
                </Link>
              </div>
              <div className="dash-ministats mt-3">
                <div className="dash-ministat">
                  <p className="t-xs">Skills extracted</p>
                  {skillsLoaded ? (
                    <p className="dash-ministat-n">{skillsExtractedCount}</p>
                  ) : skillsError ? (
                    <p className="dash-ministat-n">—</p>
                  ) : (
                    <Skel w={44} h={22} style={{ marginTop: 6 }} />
                  )}
                </div>
              </div>

              {/* the extracted skills belong with the resume they came from,
                  not in their own section further down the page */}
              <p className="ld-eyebrow mt-3">Top skills</p>
              {skillsError ? (
                <div className="mt-2">
                  <Note tone="danger">Couldn't load skills: {skillsError}. Retrying automatically.</Note>
                </div>
              ) : skillsLoading ? (
                <div className="chip-cloud mt-2">
                  {[70, 96, 60, 84, 74, 56].map((w, i) => (
                    <Skel key={i} w={w} h={30} r={999} />
                  ))}
                </div>
              ) : skillChips.length > 0 ? (
                <div className="chip-cloud mt-2">
                  {skillChips.map((sk) => (
                    <span className="chip skill-chip" key={sk.name}>
                      {sk.name}
                      {sk.proficiency != null && <span className="skill-chip-n">{sk.proficiency}%</span>}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="t-sm mt-2">
                  We couldn't extract skills from your resume yet — check back shortly.
                </p>
              )}
            </>
          ) : (
            <EmptyBlock
              bare
              icon={<LuFileText size={20} />}
              title="No resume yet"
              sub="Upload a resume so CareerSavvy can score jobs and build tailored applications."
              action={
                <Link to="/resume-upload" className="btn btn-pill btn-mint">
                  Upload resume
                </Link>
              }
            />
          )}
        </div>

        <div className="dash-side">
          <div className="ld-glass" style={{ padding: 20 }}>
            <p className="ld-eyebrow">Interviews practiced</p>
            {!statsReady && interviewCount === "" && !practiceStats ? (
              <Skel w={56} h={34} style={{ marginTop: 10 }} />
            ) : (
              <p className="ld-stat-n mt-1">
                {/* the breakdown below is text + video, so the headline has to be
                    their sum — the legacy count is text-only and contradicted it */}
                {practiceStats
                  ? practiceStats.textCount + practiceStats.videoCount
                  : interviewCount || 0}
              </p>
            )}
            {practiceStats ? (
              <p className="t-xs mt-1">
                {practiceStats.textCount} text · {practiceStats.videoCount} video
                {practiceStats.avgScore !== null ? ` · avg score ${practiceStats.avgScore}` : ""}
              </p>
            ) : null}
            <Link to="/interview-prep" className="dash-link mt-1">
              See history
              <LuChevronRight size={14} />
            </Link>
          </div>
          <div className="ld-glass" style={{ padding: 20 }}>
            <p className="ld-eyebrow">Jobs applied</p>
            <p className="ld-stat-n mt-1">{jobsAppliedDisplay}</p>
            {/* "applied by others recently" removed — the backend returned a random
               number that changed on every load (Pavithra Dashboard #3). */}
          </div>
        </div>
      </section>

      {/* section 2 — recent activity + quick actions */}
      <section className="dash-cols">
        <div className="ld-glass" style={{ padding: 20 }}>
          <h2 className="t-h3">Recent activity</h2>
          {activityList.length > 0 ? (
            <ul className="mt-2">
              {activityList.map((item, index) => {
                const Icon = activityIcon(item?.activity);
                return (
                  <li className="act-row" key={index}>
                    <span className="act-ico">
                      <Icon size={15} />
                    </span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <p
                        className="t-sm t-ink act-text"
                        style={{ fontWeight: 500 }}
                      >
                        {jobIdIn(item?.activity) ? (
                          <Link to={`/job/detailed?id=${encodeURIComponent(jobIdIn(item.activity))}`}>
                            {readableActivity(item.activity)}
                          </Link>
                        ) : (
                          readableActivity(item?.activity)
                        )}
                      </p>
                    </div>
                    {item?.time_ago && (
                      <span className="t-xs" style={{ flex: "0 0 auto" }}>
                        {item.time_ago}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : activitiesMaybeLoading ? (
            <div className="stack-3 mt-2">
              {[0, 1, 2, 3].map((i) => (
                <div className="row" key={i} style={{ gap: 12 }}>
                  <Skel w={36} h={36} r={12} />
                  <div className="grow stack-2" style={{ gap: 6 }}>
                    <Skel w="70%" h={12} />
                    <Skel w={90} h={10} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyBlock
              bare
              title="No activity yet"
              sub="Apply to a job or run a practice interview and it will show up here."
              action={
                <Link to="/search-job" className="btn btn-ghost btn-pill">
                  Browse jobs
                </Link>
              }
            />
          )}
        </div>

        <div className="ld-glass" style={{ padding: 20 }}>
          <h2 className="t-h3">Quick actions</h2>
          <ul className="mt-2" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {QUICK_ACTIONS.map((qa) => {
              const Icon = qa.icon;
              return (
                <li key={qa.label}>
                  <Link className="qa-row" to={qa.to}>
                    <Icon size={16} className="qa-ic" />
                    <span className="grow" style={{ minWidth: 0 }}>
                      <span className="t-sm t-ink" style={{ display: "block", fontWeight: 500 }}>
                        {qa.label}
                      </span>
                      <span
                        className="t-xs"
                        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {qa.sub}
                      </span>
                    </span>
                    <LuChevronRight size={16} className="qa-chev" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

    </div>
  );
}
